import { useState, useEffect } from "react";
import { useSelectorApp, useDispatchApp } from "../lib/redux";
import {
  closeCreativeNotification,
  isCreativeNotification,
} from "../actions/creativeJobs";
import { removeNotification } from "../actions/alerts";
import { ALERTS_ASYNC_STATUS } from "../utils/constants";
import {
  getActivityNotificationDismissId,
  isDismissableActivityNotification,
  sortNotificationsByRecency,
} from "../utils/notifications";

/**
 * Custom hook to manage notifications state and actions
 * @returns {Object} Notifications state and handlers
 */
export const useNotifications = () => {
  const dispatch = useDispatchApp();
  const notifications = useSelectorApp(
    (state) => state.alerts?.notifications || []
  );
  const [activeAlerts, setActiveAlerts] = useState([]);

  const hasActiveProcessing = activeAlerts.some(
    (a) =>
      a?.status === ALERTS_ASYNC_STATUS.LOADING ||
      a?.status === ALERTS_ASYNC_STATUS.GENERATING
  );

  useEffect(() => {
    setActiveAlerts(sortNotificationsByRecency(notifications));
  }, [notifications]);

  const dismissNotification = ({
    notification,
    alertId,
    dispatch: dispatchFn,
  }) => {
    if (isCreativeNotification(notification)) {
      dispatchFn(closeCreativeNotification(alertId));
      return;
    }

    dispatchFn(removeNotification(alertId));
  };

  const onCloseAlert = (alertId) => {
    const notification = notifications.find((alert) => alert.id === alertId);

    setActiveAlerts((previousAlerts) =>
      sortNotificationsByRecency(
        previousAlerts.filter((alert) => alert.id !== alertId)
      )
    );

    dismissNotification({
      notification,
      alertId,
      dispatch,
    });
  };

  const onCloseAllAlerts = () => {
    dispatch((innerDispatch, getState) => {
      const currentNotifications = getState()?.alerts?.notifications || [];
      const dismissedIds = new Set();

      currentNotifications.forEach((notification) => {
        if (!isDismissableActivityNotification(notification)) return;

        const alertId = getActivityNotificationDismissId(notification);
        if (!alertId || dismissedIds.has(alertId)) return;

        dismissedIds.add(alertId);
        dismissNotification({
          notification,
          alertId,
          dispatch: innerDispatch,
        });
      });

      if (dismissedIds.size === 0) return;

      setActiveAlerts((previousAlerts) =>
        sortNotificationsByRecency(
          previousAlerts.filter((alert) => {
            const alertId = getActivityNotificationDismissId(alert);
            return !alertId || !dismissedIds.has(alertId);
          })
        )
      );
    });
  };

  return {
    activeAlerts,
    hasActiveProcessing,
    onCloseAlert,
    onCloseAllAlerts,
  };
};
